import {
  TextInput,
  NumberInput,
  Textarea,
  Stack,
  Select,
  Switch,
  Group,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { ReactNode, useCallback, useMemo } from "react";
import { PhysicianReportDelivery } from "../../../services/admin/physicianreportdelivery.service";
import {
  required,
  email,
  phone,
  fax,
  scrollToFirstError,
} from "../../../utils/formValidation";

export interface PhysicianReportDeliveryFormValues {
  physicianId: string;
  reportDeliveryMethodId: string;
  practiceId: string;
  email: string;
  phone: string;
  fax: string;
  address: string;
  remoteConnectionIdentifier: string;
  hl7Host: string;
  hl7Port: number | "";
  hl7Facility: string;
  hl7ApplicationId: string;
  isActive: boolean;
}

interface PhysicianReportDeliveryFormProps {
  initialValues?: PhysicianReportDelivery;
  onSubmit: (values: PhysicianReportDeliveryFormValues) => void;
  physicians: Array<{ value: string; label: string }>;
  deliveryMethods: Array<{ value: string; label: string }>;
  onSelectionChange?: (selection: {
    physicianId: string;
    reportDeliveryMethodId: string;
  }) => void;
  methodWarning?: ReactNode;
}

function getMethodName(
  methodId: string,
  methods: Array<{ value: string; label: string }>
): string {
  const method = methods.find((m) => m.value === methodId);
  return method?.label?.toLowerCase() || "";
}

export default function PhysicianReportDeliveryForm({
  initialValues,
  onSubmit,
  physicians,
  deliveryMethods,
  onSelectionChange,
  methodWarning,
}: PhysicianReportDeliveryFormProps) {
  const form = useForm<PhysicianReportDeliveryFormValues>({
    initialValues: {
      physicianId: initialValues?.physicianId || "",
      reportDeliveryMethodId: initialValues?.reportDeliveryMethodId || "",
      practiceId: "",
      email: initialValues?.email || "",
      phone: initialValues?.phone || "",
      fax: initialValues?.fax || "",
      address: initialValues?.address || "",
      remoteConnectionIdentifier:
        initialValues?.remoteConnectionIdentifier || "",
      hl7Host: initialValues?.hl7Host || "",
      hl7Port: initialValues?.hl7Port ?? "",
      hl7Facility: initialValues?.hl7Facility || "",
      hl7ApplicationId: initialValues?.hl7ApplicationId || "",
      isActive: initialValues?.isActive ?? true,
    },
    validateInputOnBlur: true,
    validate: (values) => {
      const methodName = getMethodName(
        values.reportDeliveryMethodId,
        deliveryMethods
      );

      let emailError: string | null = null;
      if (methodName === "email") {
        emailError =
          required("Email is required")(values.email) || email()(values.email);
      } else {
        // Portal no longer collects an email here — the portal access grants
        // carry the recipients. Only run format validation if the user typed
        // something for non-portal methods.
        emailError = email()(values.email);
      }

      let faxError: string | null = null;
      if (methodName === "fax") {
        faxError =
          required("Fax number is required")(values.fax) || fax()(values.fax);
      } else {
        faxError = fax()(values.fax);
      }

      let addressError: string | null = null;
      if (methodName === "mail" || methodName === "ship physical copy") {
        addressError = required("Mailing address is required")(values.address);
      }

      let remoteConnectionIdentifierError: string | null = null;
      if (
        methodName.includes("remote print") &&
        !values.remoteConnectionIdentifier?.trim()
      ) {
        remoteConnectionIdentifierError =
          "Connection identifier is required for Remote Print delivery";
      }

      let hl7HostError: string | null = null;
      let hl7PortError: string | null = null;
      if (methodName.startsWith("hl7")) {
        if (!values.hl7Host?.trim()) {
          hl7HostError = "HL7 endpoint host is required";
        }
        if (!values.hl7Port) {
          hl7PortError = "HL7 endpoint port is required";
        }
      }

      return {
        physicianId: required("Physician is required")(values.physicianId),
        reportDeliveryMethodId: required("Delivery method is required")(
          values.reportDeliveryMethodId
        ),
        email: emailError,
        fax: faxError,
        address: addressError,
        phone: phone()(values.phone),
        remoteConnectionIdentifier: remoteConnectionIdentifierError,
        hl7Host: hl7HostError,
        hl7Port: hl7PortError,
      };
    },
  });

  const handleSubmit = form.onSubmit(
    useCallback(
      (values: PhysicianReportDeliveryFormValues) => onSubmit(values),
      [onSubmit]
    ),
    useCallback(() => scrollToFirstError(form), [form])
  );

  const handlePhysicianChange = useCallback(
    (value: string | null) => {
      const physicianId = value || "";
      form.setFieldValue("physicianId", physicianId);
      form.setFieldValue("practiceId", "");
      onSelectionChange?.({
        physicianId,
        reportDeliveryMethodId: form.values.reportDeliveryMethodId,
      });
    },
    [form, onSelectionChange]
  );

  const handleMethodChange = useCallback(
    (value: string | null) => {
      const reportDeliveryMethodId = value || "";
      form.setFieldValue("reportDeliveryMethodId", reportDeliveryMethodId);
      onSelectionChange?.({
        physicianId: form.values.physicianId,
        reportDeliveryMethodId,
      });
    },
    [form, onSelectionChange]
  );

  const selectedMethodName = useMemo(
    () => getMethodName(form.values.reportDeliveryMethodId, deliveryMethods),
    [form.values.reportDeliveryMethodId, deliveryMethods]
  );

  const showEmailField = useMemo(
    () => selectedMethodName === "email",
    [selectedMethodName]
  );
  const showPortalDescription = useMemo(
    () => selectedMethodName.startsWith("portal"),
    [selectedMethodName]
  );
  const showFaxField = useMemo(
    () => selectedMethodName === "fax",
    [selectedMethodName]
  );
  const showAddressField = useMemo(
    () =>
      selectedMethodName === "mail" ||
      selectedMethodName === "ship physical copy",
    [selectedMethodName]
  );
  const showHl7Fields = useMemo(
    () => selectedMethodName.startsWith("hl7"),
    [selectedMethodName]
  );
  const showRemotePrintField = useMemo(
    () => selectedMethodName.includes("remote print"),
    [selectedMethodName]
  );
  const showPhoneField = useMemo(
    () =>
      !!selectedMethodName &&
      !selectedMethodName.startsWith("portal") &&
      !selectedMethodName.startsWith("hl7") &&
      !selectedMethodName.startsWith("email") &&
      !selectedMethodName.startsWith("fax"),
    [selectedMethodName]
  );

  return (
    <form noValidate onSubmit={handleSubmit}>
      <Stack gap="md">
        <Select
          label="Physician"
          placeholder="Select physician"
          withAsterisk
          searchable
          data={physicians}
          name="physicianId"
          value={form.values.physicianId || null}
          onChange={handlePhysicianChange}
          error={form.errors.physicianId}
        />

        <Select
          label="Delivery Method"
          placeholder="Select delivery method"
          withAsterisk
          searchable
          data={deliveryMethods}
          name="reportDeliveryMethodId"
          value={form.values.reportDeliveryMethodId || null}
          onChange={handleMethodChange}
          error={form.errors.reportDeliveryMethodId}
        />

        {methodWarning}

        {showEmailField && (
          <TextInput
            label="Email Address"
            placeholder="Enter email address"
            type="email"
            withAsterisk
            name="email"
            {...form.getInputProps("email")}
          />
        )}

        {showPortalDescription && (
          <Text size="sm" c="dimmed">
            Reports will be routed to the physician's portal. Manage portal
            contacts in the Portal Access section on the physician's record.
          </Text>
        )}

        {showFaxField && (
          <TextInput
            label="Fax Number"
            placeholder="Enter fax number"
            withAsterisk
            name="fax"
            {...form.getInputProps("fax")}
          />
        )}

        {showAddressField && (
          <Textarea
            label="Mailing Address"
            placeholder="Enter mailing address"
            minRows={2}
            withAsterisk
            name="address"
            {...form.getInputProps("address")}
          />
        )}

        {showHl7Fields && (
          <>
            <Group grow>
              <TextInput
                label="HL7 Endpoint Host"
                placeholder="e.g. 192.168.1.100 or ehr.hospital.com"
                description="HL7 server hostname or IP address"
                withAsterisk
                {...form.getInputProps("hl7Host")}
              />
              <NumberInput
                label="HL7 Endpoint Port"
                placeholder="e.g. 2575"
                description="HL7 MLLP port (typically 2575)"
                min={1}
                max={65535}
                withAsterisk
                {...form.getInputProps("hl7Port")}
              />
            </Group>
            <Group grow>
              <TextInput
                label="HL7 Facility ID"
                placeholder="Receiving facility identifier"
                {...form.getInputProps("hl7Facility")}
              />
              <TextInput
                label="HL7 Application ID"
                placeholder="Receiving application identifier"
                {...form.getInputProps("hl7ApplicationId")}
              />
            </Group>
          </>
        )}

        {showRemotePrintField && (
          <TextInput
            label="Connection Identifier"
            placeholder="e.g. clinic-printer-001"
            description="Must match the identifier in the Remote Print client app at the clinic"
            withAsterisk
            name="remoteConnectionIdentifier"
            {...form.getInputProps("remoteConnectionIdentifier")}
          />
        )}

        {showPhoneField && (
          <TextInput
            label="Phone (optional)"
            placeholder="Enter phone number"
            name="phone"
            {...form.getInputProps("phone")}
          />
        )}

        <Switch
          label="Active"
          {...form.getInputProps("isActive", { type: "checkbox" })}
        />
      </Stack>
    </form>
  );
}
